/**
 * `CaseFilesSection` — the consolidated, modality-grouped view of a case's
 * ingested content ("Acervo" in pt-BR, "The File" in en-US).
 *
 * Plan: `ai-docs/lexide-acervo-consolidated-view-plan.md` §6 (F1/F4/F7/F8).
 * It replaces the ENTRADA/FONTES split — two disconnected trees where the same
 * content appeared twice under unrelated names — with ONE view over the
 * `sources/` tier, grouped by the modality the SERVER routed, labelled by the
 * meaning a lawyer recognises, and searchable by the filename they actually
 * uploaded.
 *
 * Structure:
 *   ▾ 📚 ACERVO 95                       ← the tier ROOT (uppercase)
 *      ▾ 📄 documentos 65  [A↓z] [🕒↓]   ← groups are CHILDREN (lowercase),
 *          … rows                          each with its own sort control (F7)
 *      ▾ ⚠ reprocessar                   ← obsolete, only when non-empty
 *
 * Hierarchy reads from LETTER-CASE as well as indent: the root shouts, its
 * groups do not. Operator 2026-07-31.
 *
 * What it deliberately does NOT do:
 *   - render a separate "Originais" node — the processed entry already points
 *     at its original (card "Baixar original" + the viewer), so a second
 *     folder of the same material was redundant (operator 2026-07-31,
 *     reversing D2);
 *   - group by arrival CHANNEL (D4 ruled pure modality; the channel is shown
 *     on the expanded row instead);
 *   - render a "Pendente" group — its detection rule was falsified against the
 *     live fleet (see `case-files-view.ts` rule 4);
 *   - accept drops on a group header: the groups are presentation, and a drop
 *     would imply editing the server-routed modality.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownAZ,
  ArrowUpAZ,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronsDown,
  ChevronsUp,
  ClockArrowDown,
  ClockArrowUp,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizeDisplayName } from "@/lib/workspace/display-name";
import {
  buildCaseFilesView,
  PASSTHROUGH_PREFIX,
  STALE_GROUP,
  type CaseFilesGroup,
} from "@/lib/workspace/case-files-view";
import {
  compareBySort,
  useCaseFilesSortStore,
  type SortMode,
} from "@/stores/useCaseFilesSortStore";
import { ROOT_NODE_KEY, useCaseFilesOpenStore } from "@/stores/useCaseFilesOpenStore";
import { FileRow, directorySiblings, type SiblingEntry } from "@/components/shell/CaseFileRow";
import { caseFileLabel } from "@/components/shell/CaseFileRow";
import type { CopyMoveSource } from "@/components/workspace/ResourceCopyMoveDialog";
import type { WorkspaceTreeFile, WorkspaceTreeFolder } from "@/types/gateway";

/** Emoji per known group — the folder-map convention (a 14px glyph carries the
 *  type cue so every text row can share one ink). Passthrough + stale get
 *  their own marks. */
const GROUP_EMOJI: Readonly<Record<string, string>> = {
  documents: "📄",
  images: "🖼",
  audio: "🎙",
  video: "🎬",
  office: "📊",
  whatsapp: "💬",
};

export interface CaseFilesSectionProps {
  sources: WorkspaceTreeFolder | null;
  /** Used ONLY to resolve each source's ORIGINAL provenance — never rendered
   *  as a folder (the Originais node was removed 2026-07-31). */
  rawIntake: WorkspaceTreeFolder | null;
  /** The envelope's `ingestMeta` marker — see `buildCaseFilesView` rule 3. */
  ingestMetaServed: boolean;
  /** Case scope for sort persistence; the literal `"org"` in org scope. */
  caseKey: string;
  copyMoveSource: CopyMoveSource | null;
}

/**
 * The searchable text of one file (F4).
 *
 * Deliberately includes `originalName`: the single highest-leverage fix for the
 * reported disconnection, because the lawyer searches for what they remember
 * UPLOADING, and the frozen original was renamed to a generated slug at ingest.
 */
function searchCorpusOf(f: WorkspaceTreeFile): string {
  return [f.title, f.originalName, f.tag, f.description, f.alias]
    .filter(Boolean)
    .join(" ");
}

import { useCaseSourceCatalog } from "@/hooks/useCaseSourceCatalog";
import { useCaseFolderMap } from "@/hooks/useCaseFolderMap";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { invalidateWorkspaceCaches } from "@/hooks/useCaseWorkspaceTree";
import { lensFor, useAcervoLensStore, type AcervoLens } from "@/stores/useAcervoLensStore";
import {
  UNCATEGORIZED_GROUP,
  buildCategoryFileGroups,
  categoryAxisAvailable,
} from "@/lib/workspace/case-files-category-axis";
import {
  buildNestedNodes,
  countHiddenByGroup,
  countMembers,
  nestingFactsByPath,
  type NestedNode,
} from "@/lib/workspace/case-files-nesting";

export function CaseFilesSection({
  sources,
  rawIntake,
  ingestMetaServed,
  caseKey,
  copyMoveSource,
}: CaseFilesSectionProps) {
  const { t, i18n } = useTranslation();
  // Manual refresh (operator directive 2026-08-15,
  // `lexide-workspace-refresh-miniplan.md` §3) — invalidates the SAME
  // three-cache set the auto-refresh events use, so the two paths cannot
  // drift. `caseKey` is scopeKeySegment's value (caseId | "org") by
  // construction at the CaseFileNav call site.
  const queryClient = useQueryClient();
  const connectionState = useGatewayStore((s) => s.connectionState);
  // Spin while THIS scope's tree query fetches — a real signal, not a timer.
  const treeFetching =
    useIsFetching({ queryKey: ["case-workspace-tree", caseKey] }) > 0;
  const locale = i18n.language || "pt-BR";
  const [query, setQuery] = useState("");
  const byFolder = useCaseFilesSortStore((s) => s.byFolder);
  const toggleSortFor = useCaseFilesSortStore((s) => s.toggle);
  // Expansion is PERSISTED per case (operator 2026-08-01) — subscribe to the
  // record itself so a change re-renders, and resolve each node through
  // `defaultOpenFor` rather than treating a missing key as `false`. The root
  // and the groups have OPPOSITE defaults, which is exactly what a bare
  // `!byNode[key]` would get wrong.
  const byNode = useCaseFilesOpenStore((s) => s.byNode);
  const toggleOpen = useCaseFilesOpenStore((s) => s.toggle);
  const setManyOpen = useCaseFilesOpenStore((s) => s.setMany);
  const openOf = (nodeKey: string): boolean =>
    byNode[`${caseKey}:${nodeKey}`] ?? nodeKey === ROOT_NODE_KEY;

  const view = useMemo(
    () => buildCaseFilesView(sources, rawIntake, ingestMetaServed),
    [sources, rawIntake, ingestMetaServed],
  );

  // ── S7: the second lens ────────────────────────────────────────────────
  // ⚠ The axis is OFFERED only when the serving runtime says it can back it
  // (`categoryMeta >= 1`, a LEVEL not a boolean). Absent ⇒ no switch at all,
  // which is the correct degradation: a lens the runtime cannot fill would
  // render every source as "uncategorized" and read as data loss.
  const catalogQuery = useCaseSourceCatalog();
  const folderMapQuery = useCaseFolderMap();
  const axisAvailable = categoryAxisAvailable(catalogQuery.data?.categoryMeta);
  const byCase = useAcervoLensStore((s) => s.byCase);
  const setLens = useAcervoLensStore((s) => s.set);
  const lens: AcervoLens = axisAvailable ? lensFor(byCase, caseKey) : "tipos";

  // Hoisted above the S5 block: the nesting/grouping switch below depends on
  // whether a search is active.
  const q = normalizeDisplayName(query);

  // ── S5: compound uploads are ONE unit ──────────────────────────────────
  // ⚠ ORDER MATTERS: nest FIRST, then group. A member has its own category
  // (a Pix receipt inside a chat is `financial`), so grouping first and nesting
  // second would leave it listed at top level in `Financeiro` *and* under its
  // conversation. Nesting first means only ROOTS are grouped, and a member
  // reaches the screen exactly once — through its head.
  const allFiles = useMemo(() => view.groups.flatMap((g) => g.files), [view.groups]);
  const nestingFacts = useMemo(
    () => nestingFactsByPath(catalogQuery.data?.catalog?.sources),
    [catalogQuery.data],
  );
  const factsFor = useCallback((path: string) => nestingFacts.get(path), [nestingFacts]);
  const nestedRoots = useMemo(
    () => buildNestedNodes(allFiles, factsFor),
    [allFiles, factsFor],
  );
  const nodeByPath = useMemo(() => {
    const m = new Map<string, NestedNode<WorkspaceTreeFile>>();
    for (const r of nestedRoots) m.set(r.file.path, r);
    return m;
  }, [nestedRoots]);
  const rootFiles = useMemo(() => nestedRoots.map((n) => n.file), [nestedRoots]);

  const categoryGroups = useMemo(
    () =>
      buildCategoryFileGroups(
        allFiles,
        catalogQuery.data?.catalog?.sources,
        folderMapQuery.data?.categories,
        locale,
      ),
    [allFiles, catalogQuery.data, folderMapQuery.data, locale],
  );

  // Which group each file WOULD have landed in, ignoring nesting — the input to
  // the disclosure. Computed from the UNFILTERED set on purpose: a nested
  // member is exactly the file that is missing from `categoryGroups`, so the
  // answer cannot come from there.
  const groupKeyByPath = useMemo(() => {
    const m = new Map<string, string>();
    if (lens === "categorias") {
      for (const g of categoryGroups) for (const f of g.files) m.set(f.path, g.key);
    } else {
      for (const g of view.groups) for (const f of g.files) m.set(f.path, g.key);
    }
    return m;
  }, [lens, categoryGroups, view.groups]);

  // ⚖ THE DISCLOSURE (accepted amendment, 2026-08-11). Nesting is a correct
  // re-count, but a group silently dropping 73 → 2 reads to a lawyer as "the
  // receipts were never filed". Each group says what it is holding back.
  const hiddenByGroup = useMemo(
    () => countHiddenByGroup(nestedRoots, (f) => groupKeyByPath.get(f.path) ?? UNCATEGORIZED_GROUP),
    [nestedRoots, groupKeyByPath],
  );

  // ⚠ C1 — WHAT GETS GROUPED DEPENDS ON WHETHER A SEARCH IS RUNNING.
  //
  // With no query, only ROOTS are listed and members reach the screen through
  // their head. While the box has text, EVERY file is grouped and the render
  // goes flat — because a member that is not in `visibleGroups` cannot be
  // matched at all, and a search that silently cannot find a lawyer's receipt
  // is worse than no search. The flat-render rule in `renderFileNode` is only
  // half of this; without the line below it was a comment describing behaviour
  // the data path did not have.
  const searching = q !== "";
  const groupableFiles = useMemo(
    () => (searching ? allFiles : rootFiles),
    [searching, allFiles, rootFiles],
  );
  const groupablePaths = useMemo(
    () => new Set(groupableFiles.map((f) => f.path)),
    [groupableFiles],
  );

  // ⚠ Built from ALL files, not just the groupable ones — the SKELETON has to
  // survive. `buildCategoryFileGroups` only emits groups that HAVE files, so
  // grouping roots-only made a fully-nested category (every `financial` file
  // inside a conversation) cease to exist upstream, and the `+N em conversas`
  // disclosure had no group left to render on. Filtering the FILES per group
  // keeps the group and empties it, which is what the disclosure needs — and it
  // is exactly what the `tipos` lens already does with `view.groups`.
  // The ACERVO root itself collapses, so the whole tier can be folded away.
  const rootOpen = openOf(ROOT_NODE_KEY);

  // Sibling set for the rename popover's uniqueness pre-check. Computed from
  // the REAL folder (the virtual groups are presentation; a name still has to
  // be unique in the directory it actually lives in).
  const siblings: SiblingEntry[] = useMemo(
    () => (sources ? directorySiblings(sources) : []),
    [sources],
  );

  const matches = (f: WorkspaceTreeFile) =>
    q === "" || normalizeDisplayName(searchCorpusOf(f)).includes(q);

  const groupLabel = (g: CaseFilesGroup): string =>
    // A category group carries its label already RESOLVED from the server's
    // `categories[]` (with the raw id as the honest fallback for an id the
    // registry has not registered). Never re-resolve it through the modality
    // i18n namespace — that lookup would miss and render a `caseFiles.groups.*`
    // key at the lawyer.
    (g as { label?: string }).label !== undefined
      ? (g as unknown as { label: string }).label
      : g.kind === "unknown-modality"
      ? // Verbatim server token — an unrecognised modality is a real one this
        // SPA has not labelled yet, never an error state.
        (g.rawModality ?? g.key.slice(PASSTHROUGH_PREFIX.length))
      : t(`caseFiles.groups.${g.key === STALE_GROUP ? "stale" : g.key}`);

  // Both lenses produce the SAME row shape, so everything downstream — the
  // expand/collapse-all controls, the per-group sort, the drop targets, the
  // file rows — is written once and works on either axis. Only the grouping
  // KEY differs; swapping the lens must never fork the render path.
  //
  // ⚠ The `label`/`emoji` overrides carry the CATEGORY's identity when that
  // lens is active. `groupLabel()` resolves the MODALITY vocabulary and would
  // return a `caseFiles.groups.*` miss for a categoryId, so the category label
  // — already resolved from the server's `categories[]` — is passed through
  // rather than re-derived.
  const lensGroups: { group: CaseFilesGroup; files: WorkspaceTreeFile[] }[] =
    lens === "categorias"
      ? categoryGroups.map((c) => ({
          group: {
            kind: "category",
            key: c.key,
            // ⚠ The axis module deliberately does NOT own copy, so it emits the
            // sentinel `__uncategorized__` as its own label and the CALLER must
            // translate it. Passing it through rendered the raw sentinel at the
            // lawyer — caught at the UI, which is exactly what it is for.
            // The guide mandates this bucket exists; it must read as prose.
            label:
              c.key === UNCATEGORIZED_GROUP
                ? t("caseFiles.groups.uncategorized")
                : c.label,
              emoji: c.key === UNCATEGORIZED_GROUP ? "🗂" : c.emoji,
            files: c.files.filter((f) => groupablePaths.has(f.path)),
          } as unknown as CaseFilesGroup,
          files: c.files.filter((f) => groupablePaths.has(f.path)),
        }))
      : view.groups.map((g) => ({
          // Same rule as the category lens: only ROOTS are listed at top level.
          // A member reaches the screen through its head, once.
          group: g,
          files: g.files.filter((f) => groupablePaths.has(f.path)),
        }));

  /**
   * One row, plus its compound members beneath it.
   *
   * ⚠ **A SEARCH RENDERS FLAT.** While the box has text, nesting is skipped
   * entirely (`nodeByPath` is not consulted below) — a query is a cross-cutting
   * question, and a matching member must never be hidden inside a head whose
   * own text did not match. That is the same reasoning as the search-forces-
   * groups-open rule directly above; both exist so a hit is never invisible.
   */
  const renderFileNode = (
    file: WorkspaceTreeFile,
    depth: number,
    /** The node for THIS file when it is already known (a child's subtree).
     *  ⚠ Without this the renderer re-looked-up `nodeByPath`, which holds ROOTS
     *  ONLY — so a grandchild's own children were never found and depth >= 2
     *  silently truncated, while the pure module and its test happily supported
     *  it. The contract allows chaining; the renderer must not quietly cap it. */
    known?: NestedNode<WorkspaceTreeFile>,
    /** Comparator from the owning group, so a sort reaches MEMBERS too — not
     *  just the heads. Without it, switching to A→Z visibly reorders the group
     *  and does nothing inside a conversation. */
    sortChildren?: (a: WorkspaceTreeFile, b: WorkspaceTreeFile) => number,
  ) => {
    const node = known ?? (searching ? undefined : nodeByPath.get(file.path));
    const members = node ? countMembers(node) : 0;
    const row = (
      <FileRow
        key={file.path}
        file={file}
        copyMoveSource={copyMoveSource}
        siblings={siblings}
        labelMode="title-first"
        origin={view.originByPath.get(file.path)}
        badge={members > 0 ? t("caseFiles.nesting.members", { count: members }) : undefined}
      />
    );
    if (!node || node.children.length === 0) return row;
    const nodeKey = `nest:${file.path}`;
    const expanded = openOf(nodeKey);
    return (
      <li key={file.path} data-testid="case-files-node" data-node-path={file.path}>
        <div className="flex items-start gap-1">
          <button
            type="button"
            data-testid="case-files-node-toggle"
            aria-expanded={expanded}
            onClick={() => toggleOpen(caseKey, nodeKey)}
            title={t(expanded ? "caseFiles.nesting.collapse" : "caseFiles.nesting.expand")}
            aria-label={t(expanded ? "caseFiles.nesting.collapse" : "caseFiles.nesting.expand")}
            className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-white/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:text-slate-500 dark:hover:bg-slate-700/80 dark:hover:text-slate-100"
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
          <ul className="min-w-0 flex-1">{row}</ul>
        </div>
        {expanded && (
          <ul className="ml-[7px] flex flex-col border-l border-cream-200/70 pl-3 dark:border-coffee-700/50">
            {(sortChildren
              ? [...node.children].sort((a, b) => sortChildren(a.file, b.file))
              : node.children
            ).map((c) => renderFileNode(c.file, depth + 1, c, sortChildren))}
          </ul>
        )}
      </li>
    );
  };

  const visibleGroups = lensGroups
    .map((g) => ({ group: g.group, files: g.files.filter(matches) }))
    // ⚠ A group with NO top-level rows but hidden members must still render.
    // Otherwise a category whose files are ALL inside conversations disappears
    // outright — and the `+N em conversas` pointer disappears with it, which is
    // the silent absence this disclosure exists to prevent, in its most
    // complete form. (It escapes notice on the live corpus only because 2 of
    // the 73 `financial` files happen to be standalone.) Never while searching:
    // a query's answer is its matches, not an empty group with a footnote.
    .filter((g) => g.files.length > 0 || (!searching && (hiddenByGroup.get(g.group.key) ?? 0) > 0));

  const anyResults = visibleGroups.length > 0;

  return (
    <section data-testid="case-files-section" className="flex flex-col">
      {/* Search (F4) — the nav had no filter at all before this. */}
      <div className="relative mb-1.5">
        <Search
          size={12}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
          aria-hidden
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="case-files-search"
          placeholder={t("caseFiles.searchPlaceholder")}
          aria-label={t("caseFiles.searchPlaceholder")}
          className="w-full rounded-md border border-cream-200/70 bg-white/60 py-1 pl-6 pr-6 text-[11px] text-slate-700 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:border-coffee-700/60 dark:bg-coffee-900/40 dark:text-slate-200"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            data-testid="case-files-search-clear"
            aria-label={t("actions.cancel")}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X size={11} />
          </button>
        )}
      </div>

      {/* A search with no hits is DISTINCT from a case with no content. */}
      {!anyResults && q !== "" && (
        <p
          data-testid="case-files-search-empty"
          className="px-1.5 py-2 text-[11px] text-slate-400 dark:text-slate-500"
        >
          {t("caseFiles.searchNoResults")}
        </p>
      )}

      {/* ⚠ The tier row renders even when the case is EMPTY (operator
          2026-08-07). It used to be replaced by an inviting "Nenhum arquivo
          ainda" card, which told the lawyer something they can already see and
          cost them the structure — ACERVO with a count of 0 says the same thing
          in one line and keeps the tier where it will be when files arrive.
          A SEARCH with no hits still gets its own message above: "no match for
          what you typed" is a different fact from "this case is empty". */}
      {(anyResults || q === "") && (
        <ul className="flex flex-col" data-testid="case-files-groups">
          {/* The ROOT node. Every modality group is a child of it, so the tier
              reads as one place rather than as N sibling folders competing
              with the case's other zones (operator 2026-07-31). */}
          <li data-testid="case-files-root">
            {/* A DIV wrapper, not a `w-full` button: the expand/collapse-all
                controls sit on this row and a button may not nest a button —
                the same trade the group header below already makes for its
                sort pair.
                ⚠ NO `flex-1` on the toggle, and NOT `justify-between`: the pair
                follows the COUNT and sits wherever the label ends, exactly as
                the group headers do. Right-aligning it to the row was the first
                attempt and it was wrong — the ruling below the group toggle
                already says these controls belong to the FOLDER, not to a
                right-hand column. */}
            <div className="flex items-center gap-2 rounded-md px-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800">
              <button
                type="button"
                onClick={() => toggleOpen(caseKey, ROOT_NODE_KEY)}
                aria-expanded={rootOpen}
                data-testid="case-files-root-toggle"
                className="flex min-w-0 items-center gap-1.5 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40"
              >
                {rootOpen ? (
                  <ChevronDown size={13} className="shrink-0 text-slate-400" />
                ) : (
                  <ChevronRight size={13} className="shrink-0 text-slate-400" />
                )}
                <span className="w-[14px] shrink-0 text-center text-[13px] leading-none" aria-hidden>
                  📚
                </span>
                {/* UPPERCASE marks the tier root; its children stay lowercase so
                    the hierarchy reads from letter-case, not from indent alone. */}
                <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("caseFiles.title")}
                </span>
                <span className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                  {view.count}
                </span>
              </button>

              {/* Expand-all / collapse-all (operator 2026-08-01).
                  Rendered only when the root is OPEN and there is more than one
                  group: with the tier folded they would act on rows nobody can
                  see, and with a single group they duplicate its own chevron.

                  ⚠ ALWAYS VISIBLE, deliberately — NOT hover-revealed like the
                  per-folder sort pair. That pattern earns its keep because the
                  sort control repeats down every folder and would be noise; this
                  is ONE pair on ONE row, and it was asked for as a control the
                  lawyer can SEE. A hover-only affordance on a tree nobody thinks
                  to hover is indistinguishable from a missing feature. Muted to
                  the same ink as the count so it stays quiet without hiding.

                  Icons are DOUBLE CHEVRONS because the single chevron on every
                  row already teaches the language — ⌄ open, › shut — so ⌄⌄/⌃⌃
                  reads as "all of them" without a new vocabulary. The
                  fold/unfold glyphs tried before were too fussy at 13px, and
                  `ChevronsDownUp` (arrows meeting) renders as an ✕, which beside
                  a tree root reads as close/dismiss. */}
              {/* ⚠ Was `visibleGroups.length > 1` — "with a single group they
                  duplicate its own chevron". Operator overrode that
                  (2026-08-07, asked twice): the lens switch can collapse the
                  view to ONE group, and the controls vanishing as a side effect
                  of switching lenses reads as the feature breaking, not as a
                  tidy-up. Visible whenever the tier is open. */}
              {rootOpen && (
                <div
                  data-testid="case-files-root-actions"
                  className="flex shrink-0 items-center gap-0.5"
                >
                  <button
                    type="button"
                    data-testid="case-files-expand-all"
                    onClick={() =>
                      // ⚠ GROUP keys only — `nest:<path>` nodes are deliberately
                      // left alone. "Expand all" means the groups; expanding it
                      // to conversations would dump 175 member rows into the
                      // pane, which is the state this nesting exists to end.
                      setManyOpen(caseKey, visibleGroups.map((g) => g.group.key), true)
                    }
                    title={t("caseFiles.expandAll")}
                    aria-label={t("caseFiles.expandAll")}
                    className="flex h-5 w-5 items-center justify-center rounded text-slate-400 transition-colors hover:bg-white/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:text-slate-500 dark:hover:bg-slate-700/80 dark:hover:text-slate-100"
                  >
                    <ChevronsDown size={13} />
                  </button>
                  <button
                    type="button"
                    data-testid="case-files-collapse-all"
                    onClick={() =>
                      setManyOpen(caseKey, visibleGroups.map((g) => g.group.key), false)
                    }
                    title={t("caseFiles.collapseAll")}
                    aria-label={t("caseFiles.collapseAll")}
                    className="flex h-5 w-5 items-center justify-center rounded text-slate-400 transition-colors hover:bg-white/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:text-slate-500 dark:hover:bg-slate-700/80 dark:hover:text-slate-100"
                  >
                    <ChevronsUp size={13} />
                  </button>
                  {/* Manual refresh (operator 2026-08-15): force a re-read of
                      the tier even with auto-refresh live. Same muted register
                      as the pair it joins; disabled while disconnected — an
                      inert button beats queueing a refetch that will fail into
                      an error the lawyer did not ask for. */}
                  <button
                    type="button"
                    data-testid="case-files-refresh"
                    onClick={() => invalidateWorkspaceCaches(queryClient, caseKey)}
                    disabled={connectionState !== "connected"}
                    title={t("caseFiles.refresh")}
                    aria-label={t("caseFiles.refresh")}
                    className="flex h-5 w-5 items-center justify-center rounded text-slate-400 transition-colors hover:bg-white/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 disabled:opacity-40 dark:text-slate-500 dark:hover:bg-slate-700/80 dark:hover:text-slate-100"
                  >
                    <RefreshCw size={12} className={cn(treeFetching && "animate-spin")} />
                  </button>
                </div>
              )}

              {/* ── S7 lens switch — ON THE ACERVO ROW (operator 2026-08-07).
                  It belongs to the tier it re-groups, beside that tier's own
                  count and expand controls, not floating above the search box
                  where it read as a filter over the whole pane.

                  ⚠ NOT `ml-auto`: right-aligning it to the row put it in a
                  column of its own, away from the controls it sits with.
                  Operator ruling — it FOLLOWS the expand/collapse pair with a
                  gap, the same way that pair follows the count rather than
                  being pushed to an edge.

                  Rendered ONLY when the serving runtime advertises it can back
                  the axis (`categoryMeta >= 1`, a LEVEL not a boolean). No
                  marker ⇒ no control: a lens the runtime cannot fill would show
                  every source as uncategorized, which reads as DATA LOSS rather
                  than as a missing feature. */}
              {axisAvailable && (
                <div
                  role="tablist"
                  aria-label={t("caseFiles.lens.label")}
                  data-testid="case-files-lens"
                  className="ml-2 flex shrink-0 items-center gap-0.5"
                >
                  {(["categorias", "tipos"] as const).map((l) => (
                    <button
                      key={l}
                      type="button"
                      role="tab"
                      aria-selected={lens === l}
                      data-testid={`case-files-lens-${l}`}
                      onClick={() => setLens(caseKey, l)}
                      className={cn(
                        "rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide transition-colors",
                        lens === l
                          ? "bg-cream-200/80 text-slate-600 dark:bg-coffee-700/70 dark:text-slate-200"
                          : "text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300",
                      )}
                    >
                      {t(`caseFiles.lens.${l}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {rootOpen && (
              <ul className="ml-[7px] flex flex-col border-l border-cream-200/70 pl-3 dark:border-coffee-700/50">
                {visibleGroups.map(({ group, files }) => {
                  const key = `caseFiles:${group.key}`;
                  const sort = byFolder[`${caseKey}:${key}`] ?? {
                    active: "recency" as SortMode,
                    alphaDir: "asc" as const,
                    recencyDir: "desc" as const,
                  };
                  // ⚠ A SEARCH FORCES EVERY MATCHING GROUP OPEN, without
                  // touching the stored state. Collapsed-by-default (2026-08-01)
                  // would otherwise break search outright: the lawyer types a
                  // filename, the group that contains it stays shut, and the
                  // view answers "found something, in here somewhere" — which
                  // is worse than no results, because it looks like a hit. The
                  // override is render-only, so clearing the box restores
                  // exactly the tree they had folded.
                  const isOpen = q !== "" || openOf(group.key);
                  const label = groupLabel(group);
                  // One comparator for the group AND its nested members, so a
                  // sort means the same thing at every depth.
                  const cmp = (a: WorkspaceTreeFile, b: WorkspaceTreeFile) =>
                    compareBySort(a, b, sort, (f) => caseFileLabel(f), locale);
                  const sorted = [...files].sort(cmp);
                  return (
                    <li
                      key={group.key}
                      data-testid={`case-files-group-${group.key}`}
                      data-group-kind={group.kind}
                    >
                      {/* NOT `justify-between` and no `flex-1` on the toggle:
                          the sort pair follows the COUNT with a little spacing
                          and sits wherever the label ends — it belongs to the
                          folder, not to a right-hand column (ruled). */}
                      <div className="flex items-center gap-2 rounded-md px-1.5">
                        <button
                          type="button"
                          onClick={() => toggleOpen(caseKey, group.key)}
                          aria-expanded={isOpen}
                          data-testid="case-files-group-toggle"
                          className="flex min-w-0 items-center gap-1.5 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40"
                        >
                          {isOpen ? (
                            <ChevronDown size={13} className="shrink-0 text-slate-400" />
                          ) : (
                            <ChevronRight size={13} className="shrink-0 text-slate-400" />
                          )}
                          {group.key === STALE_GROUP ? (
                            <AlertTriangle size={13} className="shrink-0 text-amber-500" aria-hidden />
                          ) : (
                            <span className="w-[14px] shrink-0 text-center text-[13px] leading-none" aria-hidden>
                              {GROUP_EMOJI[group.key] ?? "📁"}
                            </span>
                          )}
                          <span
                            className={cn(
                              "truncate text-[12px] lowercase",
                              group.key === STALE_GROUP
                                ? "text-amber-700 dark:text-amber-400"
                                : "text-slate-600 dark:text-slate-300",
                              group.kind === "unknown-modality" && "font-mono",
                            )}
                            title={label}
                          >
                            {label}
                          </span>
                          <span className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                            {files.length}
                          </span>
                          {/* ⚖ THE DISCLOSURE. This group is not listing N files
                              because they are nested inside a compound upload.
                              Without it the group just reads a smaller number,
                              and a lawyer who knows the case holds those files
                              concludes they were never filed — a false claim
                              built from a negative. Muted, never a badge: it is
                              a pointer, not an alert. */}
                          {(hiddenByGroup.get(group.key) ?? 0) > 0 && (
                            <span
                              data-testid="case-files-hidden-count"
                              data-hidden-count={hiddenByGroup.get(group.key)}
                              className="shrink-0 text-[10px] tabular-nums text-slate-400/80 dark:text-slate-500/80"
                            >
                              {t("caseFiles.nesting.hidden", {
                                count: hiddenByGroup.get(group.key) ?? 0,
                              })}
                            </span>
                          )}
                        </button>

                        {/* Sort controls appear ONLY on an EXPANDED folder
                            (operator 2026-07-31). Repeated down a column of
                            collapsed folders the pair is pure noise — and a
                            control that reorders rows you cannot see has
                            nothing to act on anyway. */}
                          {/* ⚠ NO `content-visibility: auto` here. It was added as a
                            speculative paint optimisation and caused a real,
                            user-visible bug: `content-visibility` implies PAINT
                            CONTAINMENT, which makes the element a containing
                            block for `position: fixed` descendants — so the
                            row's details modal resolved its `inset-0` against
                            THIS list and rendered trapped inside the side pane
                            (measured: 347×1430 instead of the 1600×787
                            viewport). The plan's own rule was to escalate only
                            on MEASURED jank; this was never measured. */}
                      {isOpen && (
                        <span
                          role="group"
                          aria-label={t("caseFiles.sort.groupLabel")}
                          className="flex shrink-0 items-center gap-1.5"
                        >
                          <SortButton
                            testid="case-files-sort-alpha"
                            active={sort.active === "alpha"}
                            label={t(sort.alphaDir === "asc" ? "caseFiles.sort.alphaAsc" : "caseFiles.sort.alphaDesc")}
                            Icon={sort.alphaDir === "asc" ? ArrowDownAZ : ArrowUpAZ}
                            onClick={() => toggleSortFor(caseKey, key, "alpha")}
                          />
                          <SortButton
                            testid="case-files-sort-recency"
                            active={sort.active === "recency"}
                            label={t(sort.recencyDir === "desc" ? "caseFiles.sort.recencyDesc" : "caseFiles.sort.recencyAsc")}
                            Icon={sort.recencyDir === "desc" ? ClockArrowDown : ClockArrowUp}
                            onClick={() => toggleSortFor(caseKey, key, "recency")}
                          />
                        </span>
                        )}
                      </div>

                      {isOpen && (
                        <ul className="ml-[7px] flex flex-col border-l border-cream-200/70 pl-3 dark:border-coffee-700/50">
                          {sorted.map((file) => renderFileNode(file, 0, undefined, cmp))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        </ul>
      )}

    </section>
  );
}

/** One sort-mode button. The glyph encodes mode AND direction, so no separate
 *  ▲/▼ affordance is needed; the active state is carried by ink + weight
 *  rather than colour alone. */
function SortButton({
  active,
  label,
  Icon,
  onClick,
  testid,
}: {
  active: boolean;
  label: string;
  Icon: typeof ArrowDownAZ;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : undefined}
      aria-pressed={active}
      title={label}
      aria-label={label}
      className={cn(
        "flex h-5 w-5 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40",
        // ⚠ Active is marked by INK ALONE — no border, no fill (operator
        // 2026-08-07, superseding the 2026-07-31 border rule). The border read
        // as a box drawn around an icon rather than as a selection, and every
        // folder carries the same pair, so a repeated outline down the tree was
        // noisier than the state it signalled. Brightening the glyph is the
        // quietest cue that still reads as "this one is on".
        active
          ? "text-slate-700 dark:text-slate-100"
          : "text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300",
      )}
    >
      <Icon size={12} />
    </button>
  );
}
